import React, { Component } from 'react';
import { getUtterance } from '../../../services/API';
import * as Sentry from '@sentry/browser';
import {logSessionInfo} from '../../../services/logger';
import { logSentryException } from '../../../services/util';
import { useGenerativeVoiceGlobal } from '../../../services/generativeVoiceService';
import { FEATURE_FLAGS } from '../../../constants/featureFlags';
import { TTS_EVENTS } from '../../../constants/constants';
import { dispatchTtsRequestEnded } from '../../../services/ttsEvents';
import { clearPendingSpeechForSkippedRequest, getAbortSpeechGeneration } from './Speech';

export default class LexaSpeech extends Component {
  constructor(props) {
    super(props);

    this.state = {
      speech_marks: null,
      speech_audio: null,
      speechProps: {
        onStart: null,
        onEnd: null,
        onBoundary: null,
        textToSpeak: null,
      },
      speaking: false,
      audioURL: null,
      speechVolume: 50,
    }
  }

    //This is where we handle a new speech request
  componentDidUpdate(prevprops){

    //If nothing we care about has changed or if we don't have speech properties, exit
    if( (prevprops.speechProps == this.props.speechProps) || (this.props.speechProps == null)){
      return;
    }

    // If the caller wants a word-by-word readback, then we use internal
    // otherwise let the avatar speak. Also have an override because Sumerian
    // eats speech marks at this point so if we need to control it, need to speak internally.
    let inText = this.props.speechProps.textToSpeak;
    if(inText !== null){
      //Invoke the text to speech API
      const includeSpeechMarks = !!this.props.speechProps.returnSpeechMarks;
      const locale = this.props.speechProps.locale;
      const useGenerative = this.props.speechProps.useGenerative || useGenerativeVoiceGlobal();
      const previousText = this.props.speechProps.previousText;
      
      const speechRequestId = this.props.speechProps._speechRequestId;
      const utteranceAbortGeneration = this.props.speechProps._abortGeneration ?? getAbortSpeechGeneration();
      const originalSpeechProps = { ...this.props.speechProps };
      const shouldSkipForAbort = () => utteranceAbortGeneration < getAbortSpeechGeneration();
      const handleSkippedUtterance = () => {
        console.log('LexaSpeech: Skipping utterance requested before abort generation', {
          utteranceGeneration: utteranceAbortGeneration,
          abortGeneration: getAbortSpeechGeneration(),
        });

        clearPendingSpeechForSkippedRequest(speechRequestId);

        // The skipped request is terminal for the thinking bubble even though onEnd is
        // intentionally not called here (avoids triggering stale TTS flows).
        dispatchTtsRequestEnded(speechRequestId);

        // Only resolve promise-based speech; don't call onEnd to avoid triggering stale TTS flows
        if(originalSpeechProps.resolveAbort){
          originalSpeechProps.resolveAbort();
        }
      };
      
      if (typeof window !== 'undefined') {
        const ttsCallStartedEvent = new CustomEvent(TTS_EVENTS.CALL_STARTED, { 
          detail: { 
            inText: inText,
            requestId: speechRequestId, // TT-1166: ties the thinking bubble to this request
          } 
        });
        
        window.dispatchEvent(ttsCallStartedEvent);
      }
      getUtterance(inText, includeSpeechMarks, locale, 16000, useGenerative, previousText)
        .then(resp => {
          // Check if this utterance was requested before the most recent abort.
          if(shouldSkipForAbort()){
            handleSkippedUtterance();
            return; // Don't send to avatar
          }
          
          //This is where we send stuff to the avatar
          this.setState({
            speechProps: { ...originalSpeechProps }
          });
          this.props.sendToAvatar({
            ...originalSpeechProps,
            // Bind this request's id so a late terminal callback (audio start/end) can
            // never dispatch the wrong id if a newer request has overwritten state.
            onStart: this.onUtteranceStart.bind(this, speechRequestId),
            onEnd: this.onUtteranceEnd.bind(this, speechRequestId),
            onBoundary: this.onWordEnd.bind(this),
            id: resp.id,
            audioURL: resp.audioURL,
            speech_marks: resp.speech_marks,
            speechText: resp.text,
          });
      })
      .catch(err => {
        if(shouldSkipForAbort()){
          handleSkippedUtterance();
          return; // Don't send to avatar
        }

        console.log(`Text: ${inText}`);
        console.warn(err);
        if (FEATURE_FLAGS.ENABLE_COMMON_SENTRY_ERROR_LOGGING) {
          logSentryException(new Error('TTS utterance fetch failure'), 'Could not get Utterance', { inText });
        }
        logSessionInfo("speech_retries_exhausted",inText);
        this.setState({
          speechProps: { ...originalSpeechProps },
        },()=>{
          this.onUtteranceStart(speechRequestId);
          this.onUtteranceEnd(speechRequestId);
        });
      });
    }
  }

  onWordEnd(word){
    if(this.state.speechProps && this.state.speechProps.onBoundary){
      this.state.speechProps.onBoundary(word);
    }
  }

  onUtteranceEnd(requestId){
    // TT-1166: prefer the request id bound at send time; fall back to state for legacy
    // callers. Binding matters because two overlapping requests would otherwise let a
    // late callback dispatch whichever id is currently in state. Covers normal end AND the
    // Coach error/timeout path (which reaches here via onFinishedPlaying -> onEnd).
    dispatchTtsRequestEnded(requestId != null ? requestId : (this.state.speechProps && this.state.speechProps._speechRequestId));

    //generate a callback function for this phrase
    // this is necessary to preserve the speech props after resetting the state
    function getOnEndCallback(speechProps){
      return function(){
        if(speechProps && speechProps.onEnd){
          speechProps.onEnd();
        }
      }
    }

    //Clear state for the next pass
    this.setState({
      speech_marks: null,
      speech_audio: null,
      speechProps: null,
      speaking: false,
      audioURL: null,
      speech_words: null,
    }, getOnEndCallback(this.state.speechProps).bind(this));
  }

  onUtteranceStart(requestId){
    // TT-1166: audio has started (or the error fast-path fired) — the wait is over.
    // Prefer the request id bound at send time; fall back to state for legacy callers.
    dispatchTtsRequestEnded(requestId != null ? requestId : (this.state.speechProps && this.state.speechProps._speechRequestId));

    // TODO(TT-1166): the bubble's ended event is now attributed to the correct request
    // (via the bound requestId), but onStart/onEnd below still fire this.state.speechProps'
    // callbacks — i.e. the newest request's, not necessarily the one that just terminated.
    // Pre-existing and out of scope here; overlapping requests could invoke the wrong
    // caller callback.
    if(this.state.speechProps && this.state.speechProps.onStart){
      this.state.speechProps.onStart();
    }
  }

  render() {
   return null;
  }
}